import React, { useEffect, useRef, useState, useCallback } from "react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import DocumentToolbarNew from "./DocumentToolbarNew";
import DocumentChatInside from "./chat/DocumentChatInside";
import DocumentHeader from "./DocumentHeader";
import StreamingText from "@/components/chat/StreamingText";
import { useUndoRedo } from "@/hooks/useUndoRedo";
import { useDocumentChat } from "@/context/DocumentChatContext";
import { saveSelection } from "@/utils/selectionManager";

// AI Inline System Imports (Feature Flag Controlled)
import { isFeatureEnabled, FEATURES } from "@/config/features";
import { AIInlineProvider } from "@/components/ai-inline";
import { AIInlineIntegration } from "@/components/ai-inline/AIInlineIntegration";

// ABNT Formatting Styles
import "@/styles/abnt-isolated.css";

interface DocumentEditorNewProps {
  document: any;
  content: string;
  onContentChange: (content: string) => void;
  headerContent?: string;
  footerContent?: string;
  onHeaderChange?: (header: string) => void;
  onFooterChange?: (footer: string) => void;
  isBlankDocument?: boolean;
  isAIWriting?: boolean;
  onAIWritingChange?: (writing: boolean) => void;
  streamingContent?: string;
  onStreamToDocument?: (payload: { mode: string; text: string }) => void;
  editPosition?: { start: number; end: number; section?: string };
  onTextSelection?: (text: string, position: { start: number; end: number }) => void;
  aiContent?: string;
  editorVersion?: number;
  onDocumentCreated?: () => void;
  onBack?: () => void;
  onSave?: () => void;
  onDownloadPDF?: () => void;
  onPrint?: () => void;
  onCopy?: () => void;
  isDownloading?: boolean;
  hideChatWidget?: boolean; // Ocultar chat flutuante quando integrado em contextos específicos
  hideHeader?: boolean; // Ocultar header quando já existe header externo (ex: CasoDetail)
  isLoadingDocument?: boolean; // Estado de carregamento do documento
  casoId?: string; // ✅ NOVO: ID do caso para integração KB
  onLoadKBData?: () => Promise<void>; // ✅ NOVO: Função para carregar dados da KB
}

const DocumentEditorNew = ({ 
  document, 
  content, 
  onContentChange, 
  headerContent,
  footerContent,
  onHeaderChange,
  onFooterChange,
  isBlankDocument = false,
  isAIWriting = false,
  onAIWritingChange,
  streamingContent,
  onStreamToDocument,
  editPosition,
  onTextSelection,
  aiContent = '',
  editorVersion = -1,
  onDocumentCreated,
  onBack,
  onSave,
  onDownloadPDF,
  onPrint,
  onCopy,
  isDownloading = false,
  hideChatWidget = false,
  hideHeader = false,
  isLoadingDocument = false,
  casoId, // ✅ NOVO
  onLoadKBData // ✅ NOVO
}: DocumentEditorNewProps) => {
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const { setActiveDocument } = useDocumentChat();
  
  // 🔄 Estado para garantir sincronização document.id <-> content
  const [isSyncing, setIsSyncing] = useState(false);
  const lastDocumentId = useRef<string | undefined>(undefined);
  const lastContentLength = useRef<number>(0);
  
  // Ref para controlar aplicação inicial do HTML (correção cirúrgica)
  const appliedInitialHTML = useRef(false);
  
  const [localContent, setLocalContent] = useState(content || '');
  const [selectedText, setSelectedText] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const lastSavedContent = useRef(content || '');
  const isInitialized = useRef(false);

  // Sistema moderno de undo/redo
  const { 
    saveSnapshot: saveSnapshotOriginal, 
    saveSnapshotBeforeOperation, 
    undo, 
    redo, 
    canUndo: canUndoHook, 
    canRedo: canRedoHook, 
    clearHistory 
  } = useUndoRedo({ 
    contentEditableRef: contentEditableRef,
    onContentChange: (htmlContent: string) => {
      setLocalContent(htmlContent);
      // 🔧 CORREÇÃO: Salvar HTML preservado para manter formatação ABNT
      onContentChange(htmlContent);
      lastSavedContent.current = htmlContent;
    }
  });

  // Memoizar saveSnapshot para evitar loops no useEffect
  const saveSnapshot = useCallback((content: string, force?: boolean) => {
    saveSnapshotOriginal(content, force);
  }, [saveSnapshotOriginal]);

  // Função para sanitizar conteúdo APENAS para impressão/PDF (não para salvar)
  const sanitizeForPrint = useCallback((htmlContent: string): string => {
    try {
      if (!htmlContent || typeof htmlContent !== 'string') {
        return '';
      }
      
      // Verificar se document está disponível
      if (typeof document === 'undefined') {
        console.warn('Document não disponível para sanitização');
        return htmlContent.replace(/<[^>]*>/g, ''); // Fallback simples
      }
      
      // Criar elemento temporário para extrair texto limpo sem tags HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const cleanText = tempDiv.innerText || tempDiv.textContent || '';
      
      // Normalizar quebras de linha e espaços
      return cleanText
        .replace(/\n\s*\n/g, '\n\n') // Normaliza quebras de linha duplas
        .trim();
    } catch (error) {
      console.error('Erro na sanitização:', error);
      // Fallback: remover tags HTML de forma simples
      return htmlContent.replace(/<[^>]*>/g, '').trim();
    }
  }, []);

  // 🔄 Sincronização document.id <-> content (CRÍTICO para troca de documentos)
  useEffect(() => {
    const currentDocId = document?.id;
    const currentContentLength = (content ?? '').length;
    const hasDocIdChanged = currentDocId !== lastDocumentId.current;
    const hasContentArrived = lastContentLength.current === 0 && currentContentLength > 0;
    
    if (hasDocIdChanged) {
      console.log('🔄 Documento mudou, iniciando sincronização:', {
        anterior: lastDocumentId.current,
        novo: currentDocId,
        contentLength: currentContentLength
      });
      
      // Marca como "sincronizando" para bloquear aplicação de conteúdo
      setIsSyncing(true);
      
      // Reset de flags
      appliedInitialHTML.current = false;
      isInitialized.current = false;
      
      // Atualiza referências
      lastDocumentId.current = currentDocId;
      lastContentLength.current = currentContentLength;
      
      // Aguarda DOIS ticks para garantir que o content foi atualizado
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsSyncing(false);
          console.log('✅ Sincronização completa (2 ticks), liberando aplicação de conteúdo');
        });
      });
    } else if (hasContentArrived && appliedInitialHTML.current) {
      // 🔥 CASO ESPECIAL: Content chegou DEPOIS de já ter aplicado template inicial
      console.log('🔥 Conteúdo chegou atrasado, forçando reaplicação:', {
        contentLength: currentContentLength,
        previousLength: lastContentLength.current
      });
      
      appliedInitialHTML.current = false; // Permite reaplicação
      lastContentLength.current = currentContentLength;
    } else {
      // Apenas atualizar referência de tamanho
      lastContentLength.current = currentContentLength;
    }
  }, [document?.id, content]);

  // Aplicação inicial do HTML (correção cirúrgica - só UMA vez por documento)
  useEffect(() => {
    // 🚫 BLOQUEAR se estiver sincronizando, carregando ou já aplicado
    if (!contentEditableRef.current || appliedInitialHTML.current || isSyncing || isLoadingDocument) {
      if (isSyncing) {
        console.log('⏳ Aguardando sincronização document.id <-> content...');
      }
      if (isLoadingDocument) {
        console.log('⏳ Aguardando carregamento do documento...');
      }
      return;
    }

    console.log('📝 Aplicando conteúdo inicial/atualizado:', {
      documentId: document?.id,
      contentLength: (content ?? '').length,
      appliedBefore: appliedInitialHTML.current,
      isSyncing,
      isLoadingDocument
    });

    const hasContent = (content ?? "").trim().length > 0;
    
    // Tratar documento em branco PRIMEIRO
    if (isBlankDocument && !hasContent) {
      console.log('📄 Documento em branco detectado, aplicando editor vazio');
      contentEditableRef.current.innerHTML = '';
      setLocalContent('');
      appliedInitialHTML.current = true;
      lastSavedContent.current = '';
      isInitialized.current = true;
      return;
    }
    
    if (hasContent) {
      // Aplicar conteúdo diretamente sem modificações
      contentEditableRef.current.innerHTML = content;
      setLocalContent(content);
      console.log('✅ Conteúdo aplicado do prop:', content.length, 'chars');
      
      appliedInitialHTML.current = true;
      lastSavedContent.current = content;
      
      // Salvar snapshot inicial
      saveSnapshot(content, true);
      
    } else if (document?.id) {
      // 🚫 NÃO APLICAR TEMPLATE se houver documento.id (aguardar conteúdo chegar)
      console.log('⏳ Documento com ID presente, aguardando conteúdo do banco...');
      return; // ⚡ EARLY RETURN - não marca como aplicado
      
    } else {
      // Apenas para documentos NOVOS (sem ID) aplicar template minimalista
      const initialHTML = `<div>
        <p style="text-align: justify; margin: 0.5em 0; text-indent: 1.25cm;">Comece a escrever seu documento aqui...</p>
      </div>`;
      
      contentEditableRef.current.innerHTML = initialHTML;
      setLocalContent('');
      console.log('📝 Template minimalista aplicado (documento NOVO sem ID)');
      
      appliedInitialHTML.current = true;
      lastSavedContent.current = '';
    }
    
    isInitialized.current = true;
    
    // Atualizar documento ativo no chat quando carregar
    if (document?.id && content) {
      setActiveDocument(document.id, content);
    }
  }, [contentEditableRef.current, document?.id, content, isSyncing, isLoadingDocument, saveSnapshot, setActiveDocument]);

  // 🛡️ Fallback de segurança: Reaplicar conteúdo se detectar template inicial indevido
  useEffect(() => {
    if (!contentEditableRef.current || !document?.id || isSyncing || isLoadingDocument) return;
    
    const currentHTML = contentEditableRef.current.innerHTML;
    const hasTemplateText = currentHTML.includes('Comece a escrever seu documento aqui');
    const hasRealContent = (content ?? '').trim().length > 0;
    
    if (hasTemplateText && hasRealContent) {
      console.warn('🛡️ FALLBACK: Template inicial detectado com conteúdo disponível, corrigindo...');
      contentEditableRef.current.innerHTML = content;
      setLocalContent(content);
      appliedInitialHTML.current = true;
      saveSnapshot(content, true);
      toast.success('Conteúdo restaurado');
    }
  }, [content, document?.id, isSyncing, isLoadingDocument, saveSnapshot]);

  // Aplicar conteúdo da IA quando chegar (streaming token-by-token)
  useEffect(() => {
    if (aiContent && aiContent.trim().length > 0) {
      console.log('🤖 Aplicando conteúdo da IA:', aiContent.length, 'chars');
      // Salvar snapshot antes de aplicar conteúdo da IA
      saveSnapshotBeforeOperation(localContent);
      
      setLocalContent(aiContent);
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = aiContent;
      }
      lastSavedContent.current = aiContent;
      // Salvar snapshot após aplicar conteúdo da IA
      saveSnapshot(aiContent, true);
      onDocumentCreated?.();
    }
  }, [aiContent, onDocumentCreated, localContent, saveSnapshotBeforeOperation, saveSnapshot]);

  // Scroll para o topo quando o documento é carregado
  useEffect(() => {
    if (document?.id && appliedInitialHTML.current) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
      });
    }
  }, [document?.id]);

  // Handler para mudanças de conteúdo
  const handleContentChange = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const newHtmlContent = element.innerHTML;
    const newTextContent = element.innerText || '';
    
    console.log('📝 Conteúdo alterado:', newTextContent.length, 'chars');
    
    // Manter localContent como HTML para consistência
    setLocalContent(newHtmlContent);
    
    // Atualizar documento ativo no chat
    if (document?.id) {
      setActiveDocument(document.id, newHtmlContent);
    }
    
    // Salvar snapshot para undo/redo (usando HTML completo)
    saveSnapshot(newHtmlContent);
    
    // Debounce para evitar muitas chamadas
    const timeoutId = setTimeout(() => {
      if (newTextContent !== lastSavedContent.current) {
        try {
          // 🔧 CORREÇÃO: Salvar HTML completo para preservar formatação ABNT
          onContentChange(newHtmlContent);
          lastSavedContent.current = newHtmlContent;
          console.log('💾 Conteúdo salvo com formatação:', newHtmlContent.length, 'chars');
        } catch (error) {
          console.error('Erro ao salvar conteúdo:', error);
          // Fallback: usar conteúdo HTML 
          onContentChange(newHtmlContent);
          lastSavedContent.current = newHtmlContent;
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [onContentChange, saveSnapshot]);

  // Ref estável para onTextSelection
  const onTextSelectionRef = useRef(onTextSelection);
  onTextSelectionRef.current = onTextSelection;

  // Handler para seleção de texto
  const handleTextSelection = useCallback(() => {
    // 🔧 CORREÇÃO: Guards robustos para evitar TypeError
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    const selection = window.getSelection();
    const element = contentEditableRef.current;
    
    if (!selection || !element || !element.contains || typeof element.contains !== 'function') return;
    if (!selection.anchorNode || !element.contains(selection.anchorNode)) return;
    
    const selectedText = selection.toString().trim();
    if (selectedText.length > 0) {
      setSelectedText(selectedText);
      
      // Calcular posição
      const range = selection.getRangeAt(0);
      const preSelectionRange = range.cloneRange();
      preSelectionRange.selectNodeContents(element);
      preSelectionRange.setEnd(range.startContainer, range.startOffset);
      const start = preSelectionRange.toString().length;
      const end = start + selectedText.length;
      
      onTextSelectionRef.current?.(selectedText, { start, end });
    } else {
      setSelectedText('');
    }
  }, []); // Sem dependências para estabilizar

  // Listeners para seleção
  useEffect(() => {
    // 🔧 CORREÇÃO DEFINITIVA: IIFE para isolar escopo e evitar conflito do minificador
    (() => {
      if (typeof document === 'undefined') return;
      
      const documentRef = document; // Captura explícita
      const handlerRef = handleTextSelection; // Captura explícita
      
      if (documentRef.addEventListener && typeof documentRef.addEventListener === 'function') {
        documentRef.addEventListener('selectionchange', handlerRef);
        
        // Cleanup isolado no escopo
        const cleanup = () => {
          if (documentRef.removeEventListener && typeof documentRef.removeEventListener === 'function') {
            documentRef.removeEventListener('selectionchange', handlerRef);
          }
        };
        
        return cleanup;
      }
    })();
  }, []); // Array vazio para evitar re-execução

  // Handler para foco
  const handleFocus = useCallback(() => {
    setIsEditing(true);
    console.log('✏️ Editor em foco');
  }, []);

  // Handler para blur
  const handleBlur = useCallback(() => {
    // Salvar seleção antes de perder foco para preservar em dropdowns/popovers
    if (contentEditableRef.current) {
      saveSelection(contentEditableRef.current);
      console.log('💾 Seleção salva no blur do editor');
    }
    
    setIsEditing(false);
    console.log('📤 Editor perdeu foco');
  }, []);

  // Handler para header
  const handleHeaderChange = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const newHeader = e.currentTarget.innerText || '';
    onHeaderChange?.(newHeader);
  }, [onHeaderChange]);

  // Handler para footer
  const handleFooterChange = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const newFooter = e.currentTarget.innerText || '';
    onFooterChange?.(newFooter);
  }, [onFooterChange]);

  // Sistema moderno de undo/redo
  const handleUndo = useCallback(() => {
    console.log('↶ Executando undo');
    undo();
  }, [undo]);

  const handleRedo = useCallback(() => {
    console.log('↷ Executando redo');
    redo();
  }, [redo]);

  const canUndo = useCallback(() => {
    return canUndoHook();
  }, [canUndoHook]);

  const canRedo = useCallback(() => {
    return canRedoHook();
  }, [canRedoHook]);

  // Listener removido - usando apenas o do useUndoRedo hook

  return (
    <>
      {/* Header Fixo - Considerando sidebar */}
      {!hideHeader && (
        <div className="fixed top-0 left-64 right-0 z-50 bg-white border-b">
          <DocumentHeader
            document={document}
            onBack={onBack}
            onSave={onSave}
            onDownloadPDF={onDownloadPDF}
            onPrint={onPrint}
            onCopy={() => {
              // Para cópia, usar texto limpo (sanitizado)
              const htmlContent = contentEditableRef.current?.innerHTML || '';
              const cleanText = sanitizeForPrint(htmlContent);
              navigator.clipboard.writeText(cleanText);
              onCopy?.();
            }}
            isDownloading={isDownloading}
          />
        </div>
      )}

      {/* Toolbar Fixa - Considerando sidebar */}
      <div className="fixed top-[88px] left-64 right-4 z-50 bg-white border-b">
        <div className="flex justify-center toolbar-py-minimal px-4">
        <DocumentToolbarNew 
          contentEditableRef={contentEditableRef}
          saveSnapshot={(content: string, force?: boolean) => saveSnapshot(content, force)}
          undo={handleUndo}
          redo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
        />
        </div>
      </div>

      {/* Área de Conteúdo - Offset para barras fixas */}
      <div className="pt-[20px] flex justify-center p-4 bg-gray-50 min-h-screen">
        <div className="bg-white shadow-lg max-w-4xl w-full min-h-[297mm] relative" data-doc-anchor="paper">
            {/* AI Inline Integration */}
            {isFeatureEnabled('ai_inline_editor') && (
              <>
                {(() => {
                  console.log('🚀 AI Inline: Renderizando AIInlineIntegration');
                  return null;
                })()}
                <AIInlineProvider>
                  <AIInlineIntegration
                    editorRef={contentEditableRef}
                    documentId={document?.id}
                    casoId={casoId}
                    onLoadKBData={onLoadKBData}
                  />
                </AIInlineProvider>
              </>
            )}

            {/* Header removido para evitar triplicação do título */}

            {/* Indicador de Carregamento */}
            {isLoadingDocument && (
              <div className="flex items-center justify-center p-8">
                <div className="flex flex-col items-center gap-2">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <p className="text-sm text-muted-foreground">Carregando documento...</p>
                </div>
              </div>
            )}

            {/* Área Principal do Documento */}
            {!isLoadingDocument && (
              <div 
                className="document-content-wrapper" 
                data-abnt-container="isolated"
                style={{
                  paddingLeft: '3cm',
                  paddingRight: '2cm',
                  paddingTop: '3cm',
                  paddingBottom: '2cm',
                  minHeight: '250mm',
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  isolation: 'isolate',
                  contain: 'layout style'
                }}
              >
                <div
                  ref={contentEditableRef}
                  contentEditable
                  data-abnt-editor="true"
                  data-placeholder="Comece a escrever, use a IA ou cole o seu texto aqui..."
                  data-is-blank={isBlankDocument ? 'true' : 'false'}
                  className="min-h-full outline-none editor-with-placeholder"
                  onInput={handleContentChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  suppressContentEditableWarning={true}
                  style={{
                    fontFamily: 'Times New Roman, serif',
                    fontSize: '12pt',
                    lineHeight: '1.5',
                    textAlign: 'justify',
                    isolation: 'isolate'
                  }}
                  // Conteúdo será inserido via useEffect (sem dangerouslySetInnerHTML)
                >
                  {/* O conteúdo será inserido via innerHTML */}
                </div>

                {/* Streaming Text Overlay */}
                {streamingContent && (
                  <div className="pointer-events-none">
                    <StreamingText content={streamingContent} />
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            {onFooterChange && !isLoadingDocument && (
              <>
                <Separator />
                <div
                  contentEditable
                  className="p-4 text-center text-sm text-gray-600 border-t border-gray-200 abnt-footer"
                  onInput={handleFooterChange}
                  suppressContentEditableWarning={true}
                  style={{ minHeight: '40px' }}
                >
                  {footerContent || 'Rodapé do documento...'}
                </div>
              </>
            )}
          </div>
        </div>

      {/* Chat do Documento - Posicionado no canto direito (opcional) */}
      {!hideChatWidget && (
        <div
          className="document-chat-inside-wrapper"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 999,
            maxWidth: '400px'
          }}
        >
          <DocumentChatInside />
        </div>
      )}
    </>
  );
};

export default DocumentEditorNew;